/**
 * Formatações de apresentação do formulário de extensão.
 *
 * O backend manda dado cru (CPF só com dígitos, data ISO); estas funções só
 * mudam a aparência e nunca alteram o que será enviado no submit. Todas
 * degradam para o valor original quando a entrada não bate com o formato
 * esperado, para um dado inesperado não apagar informação da tela.
 *
 * A máscara de CPF fica em `utils/cpf` (`cpfMask`), já usada pelo
 * MissingDataFormulary — não se duplica aqui.
 */

import { ILayoutField, IUploadedDocument, PersonType } from "./types";

/** "2026-07-30" → "30/07/2026". Devolve a entrada se não for ISO. */
export function formatIsoDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);

  if (!match) return isoDate;

  const [, year, month, day] = match;

  return `${day}/${month}/${year}`;
}

/** "(31) 91234-5678" a partir de dígitos; devolve a entrada se não reconhecer. */
export function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");

  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }

  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }

  return phone;
}

/** "Maria Aparecida Souza" → "MS", para o avatar do card de dependente. */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  const first = parts[0].charAt(0);
  const last = parts[parts.length - 1].charAt(0);

  return `${first}${last}`.toUpperCase();
}

/** Rótulos exatamente como o Figma escreve nos cards do Step 2. */
export function formatAccommodation(accommodation: string): string {
  if (accommodation === "ENFERMARIA") return "Enfermaria";
  if (accommodation === "QUARTO_PRIVATIVO") return "Quarto Privativo";
  if (accommodation === "QUARTO_PRIVATIVO_UTI") return "Quarto Privativo + UTI";

  return accommodation;
}

/**
 * "Filho · 14 anos" — linha de apoio do card de dependente no Step 3. No design
 * o cônjuge aparece sem idade, então ela só entra quando informada.
 */
export function formatDependentSummary(
  relationship: string,
  age?: number | null
): string {
  if (age === undefined || age === null) return relationship;

  return `${relationship} · ${age} ${age === 1 ? "ano" : "anos"}`;
}

/**
 * Chave estável de um requisito de documento. Um mesmo `fieldId` aparece uma vez
 * para o titular e uma vez por dependente, então a identidade da linha precisa
 * do CPF do beneficiário junto.
 */
export function buildDocumentKey(
  fieldId: string,
  beneficiaryTaxPayerId: string
): string {
  return `${beneficiaryTaxPayerId}::${fieldId}`;
}

export function findUploadedDocument(
  documents: IUploadedDocument[],
  fieldId: string,
  beneficiaryTaxPayerId: string
): IUploadedDocument | undefined {
  return documents.find(
    (document) =>
      document.fieldId === fieldId &&
      document.beneficiaryTaxPayerId === beneficiaryTaxPayerId
  );
}

/**
 * Todos os anexos MENOS o deste requisito. Serve ao reenvio — o anterior sai da
 * soma do limite de 50MB — e ao "Remover" do Step 4.
 */
export function excludeUploadedDocument(
  documents: IUploadedDocument[],
  fieldId: string,
  beneficiaryTaxPayerId: string
): IUploadedDocument[] {
  return documents.filter(
    (document) =>
      !(
        document.fieldId === fieldId &&
        document.beneficiaryTaxPayerId === beneficiaryTaxPayerId
      )
  );
}

export function filterLayoutFields(
  layoutFields: ILayoutField[],
  personType: PersonType
): ILayoutField[] {
  return layoutFields.filter((field) => field.personType === personType);
}

/** Soma dos anexos já enviados, para o limite de 50MB por candidato. */
export function sumDocumentSizes(documents: IUploadedDocument[]): number {
  return documents.reduce((total, document) => total + document.sizeBytes, 0);
}
